'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, UseQueryResult } from '@tanstack/react-query';
import { getSocket } from '@/lib/echo';
import { useEchoConnection } from '@/hooks/use-echo-connection';
import { ocrApi } from '../services/ocr.api';
import { ocrKeys } from '../services/ocr.queries';
import type { OcrResult, OcrResultStatus, ExtractedDataItem } from '../types';
import { mergeAcceptedFields } from '../utils/accepted-fields';

interface OcrProcessingEvent {
  ocr_result_id: number;
  status: string;
  current_page: number;
  total_pages: number;
  extracted: ExtractedDataItem[];
  /**
   * Set by the backend when `extracted` was too large to fit the broadcaster's
   * per-event size limit and got dropped from the wire. The client must then
   * refetch the authoritative result over HTTP.
   */
  extracted_truncated?: boolean;
}

const FALLBACK_POLL_MS = 5_000;
const TERMINAL_STATUSES: OcrResultStatus[] = ['completed', 'failed', 'skipped'];

function patchContainerResultsCache(
  queryClient: QueryClient,
  containerId: number,
  resultId: number,
  patch: { status: OcrResultStatus; extracted?: ExtractedDataItem[] | null }
) {
  queryClient.setQueryData<{ data: OcrResult[] }>(
    ocrKeys.containerResults(containerId),
    (old) => {
      if (!old) return old;
      return {
        ...old,
        data: old.data.map((r) =>
          r.id === resultId
            ? {
                ...r,
                status: patch.status,
                extracted_data:
                  patch.extracted && patch.extracted.length > 0
                    ? mergeAcceptedFields(r.extracted_data, patch.extracted)
                    : r.extracted_data
              }
            : r
        )
      };
    }
  );
}

/**
 * Fetches container OCR results and subscribes to WebSocket events
 * for each result. Any field-level or status change updates the cache
 * in real-time, so the sheet reflects live data.
 *
 * If the WebSocket connection fails or stays unavailable, it falls
 * back to polling — but only for results still in `pending`/`processing`,
 * never refetching the full container list.
 */
export function useContainerResultsLive(
  containerId: number | null
): UseQueryResult<{ data: OcrResult[] }> {
  const queryClient = useQueryClient();
  const subscribedIds = useRef<Set<number>>(new Set());
  const { shouldFallback } = useEchoConnection();

  const query = useQuery({
    ...ocrKeys.containerResults(containerId ?? 0),
    queryKey: ocrKeys.containerResults(containerId ?? 0),
    queryFn: () => ocrApi.containerResults(containerId!),
    enabled: containerId != null && containerId > 0
  });

  const results = useMemo(
    () => (query.data?.data ?? []) as OcrResult[],
    [query.data]
  );

  const inProgressIds = useMemo(
    () =>
      results
        .filter((r) => r.status === 'pending' || r.status === 'processing')
        .map((r) => r.id),
    [results]
  );
  const inProgressKey = inProgressIds.join(',');

  // Subscribe to WebSocket channels for each result
  useEffect(() => {
    if (!results.length) return;

    const newIds = results.map((r) => r.id);
    let cancelled = false;

    getSocket().then((socket) => {
      if (cancelled) return;

      for (const id of newIds) {
        if (subscribedIds.current.has(id)) continue;
        subscribedIds.current.add(id);

        socket
          .private(`ocr-result.${id}`)
          .listen('.ocr-processing', (event: OcrProcessingEvent) => {
            // Patch status immediately. When `extracted` is empty (e.g. a
            // truncated payload) the cache keeps its existing field data, so
            // this never wipes the panel.
            patchContainerResultsCache(queryClient, containerId!, id, {
              status: event.status as OcrResultStatus,
              extracted: event.extracted
            });

            // Also refresh the containers list (status badges)
            queryClient.invalidateQueries({
              queryKey: ocrKeys.containers()
            });

            // Refetch the authoritative result when the event dropped its
            // field data (broadcaster size cap) or when extraction completed
            // (to load chunks).
            if (event.extracted_truncated || event.status === 'completed') {
              queryClient.invalidateQueries({
                queryKey: ocrKeys.containerResults(containerId!)
              });
            }
          });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [results, containerId, queryClient]);

  // Cleanup: leave all channels on unmount
  useEffect(() => {
    return () => {
      const ids = subscribedIds.current;
      if (ids.size === 0) return;

      getSocket().then((socket) => {
        for (const id of ids) {
          socket.leave(`ocr-result.${id}`);
        }
      });
      subscribedIds.current = new Set();
    };
  }, [containerId]);

  // Fallback polling: only when WebSocket is unhealthy AND there is in-progress work.
  // Polls each in-progress id individually; never refetches the full container list.
  //
  // Page-visibility aware: the poll loop is suspended while the tab is
  // hidden (laptop asleep, user switched tabs) and resumes on visibility-
  // change. A 5 s interval running across 50 background tabs is the kind
  // of waste that adds up to a noticeable battery drain, and the moment
  // the tab becomes visible we kick off an immediate poll so the user
  // doesn't have to wait the full FALLBACK_POLL_MS for fresh data.
  useEffect(() => {
    if (!shouldFallback || containerId == null || inProgressIds.length === 0) {
      return;
    }

    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    const pollOnce = async () => {
      const ids = inProgressKey.split(',').map(Number);
      try {
        const response = await ocrApi.getStatuses(ids);
        if (cancelled) return;

        let anyTerminal = false;
        let anyCompleted = false;

        for (const fresh of response.data) {
          patchContainerResultsCache(queryClient, containerId, fresh.id, {
            status: fresh.status,
            extracted: fresh.extracted_data
          });
          if (TERMINAL_STATUSES.includes(fresh.status)) {
            anyTerminal = true;
            if (fresh.status === 'completed') anyCompleted = true;
          }
        }

        if (anyTerminal) {
          queryClient.invalidateQueries({ queryKey: ocrKeys.containers() });
        }
        // Refetch full container results once if anything completed, so chunks
        // and template relations land for the detail view.
        if (anyCompleted) {
          queryClient.invalidateQueries({
            queryKey: ocrKeys.containerResults(containerId)
          });
        }
      } catch {
        // Best-effort: swallow errors so a transient failure doesn't kill the loop.
      }
    };

    const startInterval = () => {
      if (interval !== null) return;
      interval = setInterval(pollOnce, FALLBACK_POLL_MS);
    };

    const stopInterval = () => {
      if (interval === null) return;
      clearInterval(interval);
      interval = null;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stopInterval();
      } else {
        // Refresh immediately when the user returns rather than wait
        // the full poll interval for stale in-progress rows.
        void pollOnce();
        startInterval();
      }
    };

    if (document.visibilityState !== 'hidden') {
      void pollOnce();
      startInterval();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    shouldFallback,
    containerId,
    inProgressKey,
    inProgressIds.length,
    queryClient
  ]);

  return query as UseQueryResult<{ data: OcrResult[] }>;
}
